import { portal } from "@/lib/api";
import { ApiError } from "@/lib/http";
import { loadStripe } from "@/lib/stripe";
import type { PaymentAttempt } from "@/lib/types";

/** What the browser needs to take the customer to their bank, from the portal's `PAYMENT_NEEDS_APPROVAL` answer. */
export interface Approval {
  attemptId: string;
  clientSecret: string;
  /** The saved card the bank is asked about. */
  paymentMethod: string | null;
}

/** The approval a payment needs, when that is why it did not go through (else `null`: it is some other error). */
export function approvalNeededBy(err: unknown): Approval | null {
  if (!(err instanceof ApiError) || err.reason !== "PAYMENT_NEEDS_APPROVAL") return null;
  const { attempt_id: attemptId, client_secret: clientSecret, payment_method: method } = err.details;
  if (typeof attemptId !== "string" || typeof clientSecret !== "string") return null;
  return { attemptId, clientSecret, paymentMethod: typeof method === "string" ? method : null };
}

/**
 * Takes the customer to their bank to approve a payment, then asks the portal to finish it. The same payment
 * completes, so the card is never charged a second time. Rejects, with something the customer can read, unless the
 * payment went through.
 */
export async function approvePayment(
  approval: Approval,
  orgId: string,
  publishableKey: string | null | undefined,
): Promise<PaymentAttempt> {
  if (!publishableKey) throw new Error("Card payments aren't available right now.");
  const stripe = await loadStripe(publishableKey);
  const result = await stripe.confirmCardPayment(
    approval.clientSecret,
    approval.paymentMethod ? { payment_method: approval.paymentMethod } : undefined,
  );
  // Whatever the bank said, the portal records it (and, when approved, finishes what the payment was for).
  const attempt = await portal.confirmPaymentApproval(orgId, approval.attemptId);
  if (attempt.state !== "successful") {
    throw new Error(
      result.error?.message ??
        attempt.failure_reason ??
        "Your bank didn't approve this payment. Nothing was charged.",
    );
  }
  return attempt;
}
